import {
  faChevronDown,
  faChevronUp,
  faEllipsis,
} from '@fortawesome/pro-regular-svg-icons';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { FilterSearchInput } from '#src/components/search/FilterSearchInput/index.tsx';
import { FilterSection } from '#src/components/search/FilterSection/index.tsx';
import { Option } from '#src/components/search/Option/index.tsx';
import { useFacet } from '#src/contexts/graphql-search/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';

type AtomListProps = {
  attribute: string;
  formatOption?: (
    value: string,
    formatMessage: (message: MessageDescriptor) => string
  ) => string;
  renderBefore?: (option: string) => ReactNode;
  searchable?: boolean;
  items: RefinementListItem[];
  refine: (value: string) => void;
  searchForItems?: (q: string) => void;
  hide?: (items: RefinementListItem[]) => boolean;
  isFromSearch?: boolean;
  hasExhaustiveItems?: boolean;
  isShowingMore?: boolean;
  toggleShowMore?: () => void;
  visible: boolean;
};

const ShowMoreButton = styled(Button)`
  &.compact {
    padding: 0;
    color: var(--c-nd-600);
    font-weight: unset;
  }
`;

const Truncated = styled.p`
  color: var(--c-nd-600);
`;
export interface RefineListProps
  extends Omit<UseRefinementListProps, 'limit' | 'showMoreLimit'>,
    Omit<
      AtomListProps,
      | 'items'
      | 'refine'
      | 'searchForItems'
      | 'hasExhaustiveItems'
      | 'toggleShowMore'
      | 'isShowingMore'
    > {}

const identity = (name: string) => name;

export const AtomList = ({
  attribute,
  formatOption = identity,
  renderBefore,
  searchable = false,
  refine,
  items,
  searchForItems,
  hide = undefined,
  isFromSearch,
  hasExhaustiveItems,
  toggleShowMore,
  isShowingMore,
  visible,
}: AtomListProps) => {
  const { formatMessage } = useIntl();
  const track = useEvents();
  const [search, setSearch] = useState('');

  useDebounce(() => searchForItems?.(search), 300, [search]);

  const handleChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setSearch(event.target.value);
  };

  const doRefine = (value: string) => {
    setSearch('');
    refine(value);
    track('Use Market Filter', {
      filterName: attribute,
      filterValue: value,
    });
  };

  const hidden = !isFromSearch && (!items.length || hide?.(items));

  const showMore =
    !isFromSearch && toggleShowMore && (!hasExhaustiveItems || isShowingMore);

  if (!visible) return <FilterSection hidden={hidden} />;

  return (
    <FilterSection
      hidden={hidden}
      search={
        searchable ? (
          <FilterSearchInput handleChange={handleChange} value={search} />
        ) : null
      }
    >
      {items.map(item => (
        <Option
          variant="checkbox"
          key={item.value}
          onClick={() => doRefine(item.value)}
          active={item.isRefined}
          count={item.count}
        >
          {renderBefore ? renderBefore(item.label) : null}
          <span>{formatOption(item.label, formatMessage)}</span>
        </Option>
      ))}
      {!!showMore && (
        <>
          {!!isShowingMore && !hasExhaustiveItems && (
            <Truncated>
              <FontAwesomeIcon icon={faEllipsis} />
            </Truncated>
          )}
          <ShowMoreButton
            size="compact"
            color="transparent"
            onClick={toggleShowMore}
            startIcon={
              <FontAwesomeIcon
                icon={isShowingMore ? faChevronUp : faChevronDown}
              />
            }
          >
            <FormattedMessage
              {...(isShowingMore ? glossary.showLess : glossary.showMore)}
            />
          </ShowMoreButton>
        </>
      )}
    </FilterSection>
  );
};

export const MAX_SHOW_MORE_LIMIT = 40;

export const RefineList = ({
  attribute,
  visible,
  ...rest
}: RefineListProps) => {
  const { up: isTabletOrDesktop } = useScreenSize('tablet');
  const limit = isTabletOrDesktop ? 10 : MAX_SHOW_MORE_LIMIT / 2;
  const showMoreLimit = MAX_SHOW_MORE_LIMIT;
  const {
    items,
    refine,
    searchForItems,
    isFromSearch,
    hasExhaustiveItems,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    limit: MAX_SHOW_MORE_LIMIT - 1,
    showMoreLimit,
    attribute,
    ...rest,
  });
  const { indexUiState } = useInstantSearch();

  const allItems = useMemo(() => {
    const uiRefinements = indexUiState.refinementList?.[attribute] || [];

    const optimisticRefinedItems = items.map(item => ({
      ...item,
      // we rather consider the refinement state from the UI
      // so that we don't rely on the Algolia answer to come back to reflect the checkbox state
      isRefined: !!uiRefinements.find(v => v === item.value),
    }));

    // if you load the page with more refinements than the actual
    // number of facet values retrieved by the widget, we need to
    // append them to the list, assuming counts of 0 (as we don't know the count - will be hidden).
    const extraItems = uiRefinements
      .map(v =>
        items.find(item => item.value === v)
          ? undefined
          : {
              label: v,
              value: v,
              isRefined: true,
              count: 0,
            }
      )
      .filter(Boolean);

    return [...optimisticRefinedItems, ...extraItems].slice(0, limit);
  }, [attribute, indexUiState.refinementList, items, limit]);

  return (
    <AtomList
      {...rest}
      attribute={attribute}
      visible={visible}
      refine={refine}
      items={allItems}
      searchForItems={searchForItems}
      isFromSearch={isFromSearch}
      hasExhaustiveItems={hasExhaustiveItems}
      isShowingMore={isShowingMore}
      toggleShowMore={toggleShowMore}
    />
  );
};

export const BackendRefineList = ({
  attribute,
  visible,
  sortBy,
  transformItems,
  ...rest
}: RefineListProps) => {
  const { up: isTabletOrDesktop } = useScreenSize('tablet');
  const limit = isTabletOrDesktop ? 10 : MAX_SHOW_MORE_LIMIT / 2;

  const { items, refine, toggle, search, isFromSearch } = useFacet(attribute);

  const sortedItems = transformItems
    ? transformItems(items, { results: null })
    : items;

  const searchForItems = (q: string) => {
    search(q);
  };

  return (
    <AtomList
      {...rest}
      attribute={attribute}
      visible={visible}
      refine={newValue => refine(toggle([newValue]))}
      items={sortedItems.slice(0, limit)}
      searchForItems={searchForItems}
      isFromSearch={isFromSearch}
    />
  );
};
